import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { adminService } from "../../../../services/admin-service";
import { AdminPageHeader, AdminPanel, EmptyState, StatusBadge, formatDate } from "./admin-ui";
import { candidate, canRoute, empRoute, employer } from "../../../../globals/route-names";

const emptyForm = {
    name: "",
    email: "",
    phone: "",
    password: "",
    role: "candidate",
    status: "active"
};

function AdminUsersPage({ role = "", title = "Users", subtitle = "Candidates, employers, and admins" }) {
    const [users, setUsers] = useState([]);
    const [editing, setEditing] = useState(null);
    const [creating, setCreating] = useState({ ...emptyForm, role: role || "candidate" });
    const [message, setMessage] = useState("");
    const [loading, setLoading] = useState(true);
    const navigate = useNavigate();

    const load = useCallback(() => {
        setLoading(true);
        setMessage("");
        adminService.users({ role })
            .then((data) => setUsers(data.users || []))
            .catch((error) => {
                setUsers([]);
                setMessage(error.message);
            })
            .finally(() => setLoading(false));
    }, [role]);

    useEffect(() => {
        setCreating((current) => ({ ...current, role: role || current.role || "candidate" }));
    }, [role]);

    useEffect(() => {
        load();
    }, [load]);

    const setStatus = async (user, status) => {
        try {
            await adminService.updateUserStatus(user.id, status);
            setMessage(`${user.name} is now ${status}.`);
            load();
        } catch (error) {
            setMessage(error.message);
        }
    };

    const saveUser = async () => {
        try {
            await adminService.updateUser(editing.id, {
                name: editing.name,
                phone: editing.phone,
                status: editing.status,
                role: editing.role
            });
            setMessage(`${editing.name} updated.`);
            setEditing(null);
            load();
        } catch (error) {
            setMessage(error.message);
        }
    };

    const resetPassword = async (user) => {
        const password = window.prompt(`Enter new password for ${user.name}. Minimum 6 characters.`);
        if (password === null) return;
        if (password.length < 6) {
            setMessage("Password must be at least 6 characters.");
            return;
        }
        try {
            await adminService.resetUserPassword(user.id, password);
            setMessage(`Password updated for ${user.name}.`);
        } catch (error) {
            setMessage(error.message);
        }
    };

    const createUser = async () => {
        try {
            await adminService.createUser(creating);
            setMessage(`${creating.name} created as ${creating.role}.`);
            setCreating({ ...emptyForm, role: role || "candidate" });
            load();
        } catch (error) {
            setMessage(error.message);
        }
    };

    const impersonate = async (user) => {
        try {
            const data = await adminService.impersonateUser(user.id);
            const nextUser = data.user || user;
            window.location.href = nextUser.role === "employer"
                ? empRoute(employer.DASHBOARD)
                : canRoute(candidate.DASHBOARD);
        } catch (error) {
            setMessage(error.message);
        }
    };

    return (
        <>
            <AdminPageHeader title={title} subtitle={subtitle} />
            {message && <div className="alert alert-info">{message}</div>}
            <AdminPanel title="Add Account" icon="fa fa-user-plus">
                <div className="row">
                    <div className="col-lg-3 col-md-6 m-b15">
                        <label className="form-label">Name</label>
                        <input className="form-control" value={creating.name} onChange={(event) => setCreating({ ...creating, name: event.target.value })} />
                    </div>
                    <div className="col-lg-3 col-md-6 m-b15">
                        <label className="form-label">Email</label>
                        <input className="form-control" type="email" value={creating.email} onChange={(event) => setCreating({ ...creating, email: event.target.value })} />
                    </div>
                    <div className="col-lg-2 col-md-6 m-b15">
                        <label className="form-label">Phone</label>
                        <input className="form-control" value={creating.phone} onChange={(event) => setCreating({ ...creating, phone: event.target.value })} />
                    </div>
                    <div className="col-lg-2 col-md-6 m-b15">
                        <label className="form-label">Role</label>
                        <select className="form-control" value={creating.role} onChange={(event) => setCreating({ ...creating, role: event.target.value })}>
                            <option value="candidate">Candidate</option>
                            <option value="employer">Employer</option>
                            <option value="admin">Admin</option>
                        </select>
                    </div>
                    <div className="col-lg-2 col-md-6 m-b15">
                        <label className="form-label">Password</label>
                        <input className="form-control" type="password" value={creating.password} onChange={(event) => setCreating({ ...creating, password: event.target.value })} />
                    </div>
                </div>
                <button className="site-button" type="button" onClick={createUser}>Create Account</button>
            </AdminPanel>
            <AdminPanel title="Account List" icon="fa fa-users">
                <div className="d-flex justify-content-end m-b15">
                    <button className="btn btn-sm btn-outline-primary" type="button" onClick={load} disabled={loading}>
                        {loading ? "Syncing..." : "Refresh from D1"}
                    </button>
                </div>
                <div className="table-responsive">
                    <table className="table table-bordered align-middle">
                        <thead><tr><th>Name</th><th>Email</th><th>Phone</th><th>Role</th><th>Status</th><th>Jobs Posted</th><th>Joined</th><th>Action</th></tr></thead>
                        <tbody>
                            {loading ? (
                                <tr><td colSpan="8"><EmptyState text="Syncing users from D1..." /></td></tr>
                            ) : users.length === 0 ? (
                                <tr><td colSpan="8"><EmptyState text="No users found." /></td></tr>
                            ) : users.map((user) => (
                                <tr key={user.id}>
                                    <td>{user.name}</td>
                                    <td>{user.email}</td>
                                    <td>{user.phone || ""}</td>
                                    <td><StatusBadge status={user.role} /></td>
                                    <td><StatusBadge status={user.status} /></td>
                                    <td>
                                        {user.role === "employer" ? (
                                            <button 
                                                type="button" 
                                                className="btn btn-link p-0 text-decoration-none fw-bold"
                                                onClick={() => navigate(`/admin/jobs/all?employerEmail=${encodeURIComponent(user.email)}`)}
                                            >
                                                {user.jobs_count || 0} Jobs
                                            </button>
                                        ) : (
                                            <span className="text-muted">—</span>
                                        )}
                                    </td>
                                    <td>{formatDate(user.created_at)}</td>
                                    <td>
                                        <button className="btn btn-sm btn-outline-primary me-1" type="button" onClick={() => setEditing(user)}>Edit</button>
                                        <button className="btn btn-sm btn-outline-success me-1" type="button" onClick={() => setStatus(user, "active")}>Activate</button>
                                        <button className="btn btn-sm btn-outline-danger me-1" type="button" onClick={() => setStatus(user, "suspended")}>Suspend</button>
                                        <button className="btn btn-sm btn-outline-dark me-1" type="button" onClick={() => resetPassword(user)}>Password</button>
                                        {["employer", "candidate"].includes(user.role) && (
                                            <button className="btn btn-sm btn-outline-warning" type="button" onClick={() => impersonate(user)}>
                                                Login as {user.role}
                                            </button>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </AdminPanel>
            {editing && (
                <AdminPanel title={`Edit ${editing.role}: ${editing.email}`} icon="fa fa-user-edit">
                    <div className="row">
                        <div className="col-md-4 m-b20">
                            <label className="form-label">Name</label>
                            <input className="form-control" value={editing.name || ""} onChange={(event) => setEditing({ ...editing, name: event.target.value })} />
                        </div>
                        <div className="col-md-4 m-b20">
                            <label className="form-label">Phone</label>
                            <input className="form-control" value={editing.phone || ""} onChange={(event) => setEditing({ ...editing, phone: event.target.value })} />
                        </div>
                        <div className="col-md-4 m-b20">
                            <label className="form-label">Status</label>
                            <select className="form-control" value={editing.status || "active"} onChange={(event) => setEditing({ ...editing, status: event.target.value })}>
                                <option value="active">Active</option>
                                <option value="suspended">Suspended</option>
                                <option value="inactive">Inactive</option>
                            </select>
                        </div>
                        <div className="col-md-4 m-b20">
                            <label className="form-label">Role</label>
                            <select className="form-control" value={editing.role || "candidate"} onChange={(event) => setEditing({ ...editing, role: event.target.value })}>
                                <option value="candidate">Candidate</option>
                                <option value="employer">Employer</option>
                                <option value="admin">Admin</option>
                            </select>
                        </div>
                    </div>
                    <button className="site-button me-2" type="button" onClick={saveUser}>Save User</button>
                    <button className="site-button-secondry" type="button" onClick={() => setEditing(null)}>Cancel</button>
                </AdminPanel>
            )}
        </>
    );
}

export default AdminUsersPage;
